import { $isListNode, ListNode } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isHeadingNode } from "@lexical/rich-text";
import { $isTableNode, $isTableSelection } from "@lexical/table";
import {
  $findMatchingParent,
  $getNearestNodeOfType,
  mergeRegister,
} from "@lexical/utils";
import {
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  FormatUnderlined,
  GridOn,
  ImageOutlined,
  Redo,
  Undo,
} from "@mui/icons-material";
import { Dialog } from "@mui/material";
import { captureSentryException } from "@utils";
import {
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  LexicalNode,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from "lexical";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { INSERT_IMAGE_COMMAND } from "../imageUploadPlugin";
import { InsertTableDialog } from "../tablePlugin/TablePlugin";
import ToolbarItem from "./ToolbarItem";
import ToolBarSelect from "./ToolBarSelect";
import {
  blockTypeToBlockName,
  findTopLevelElement,
  getSelectedNode,
  INITIAL_TOOLBAR_STATE,
} from "./utils";

const ToolbarPlugin = ({ padding = "px-2" }: { padding?: string }) => {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [toolbarState, setToolbarState] = useState(INITIAL_TOOLBAR_STATE);

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      editor.dispatchCommand(INSERT_IMAGE_COMMAND, { file });
    }
    e.target.value = "";
  };

  // Helper function to check if error is a Lexical context error
  const isLexicalContextError = (error: any): boolean => {
    const errorMessage = error?.message || error?.toString() || "";
    return (
      errorMessage.includes("Unable to find an active editor state") ||
      errorMessage.includes(
        "State helpers or node methods can only be used synchronously"
      ) ||
      errorMessage.includes("_TableSelection.hasFormat") ||
      errorMessage.includes("LexicalTable.dev.mjs")
    );
  };

  // Helper function to handle Lexical context errors
  // This function triggers a page reload when the specific Lexical context error occurs
  // This is necessary because this error indicates a corrupted editor state that cannot be recovered from
  const handleLexicalContextError = (error: any, context: string) => {
    console.error(
      `Lexical context error in ${context}, reloading page:`,
      error
    );
    captureSentryException(error, {
      extra: {
        toolbarState,
        editor,
        action: `page_reload_triggered_from_${context}`,
      },
    });
    // Reload the page to recover from the corrupted editor state
    window.location.reload();
  };

  const handleHeadingNode = useCallback(
    (selectedElement: LexicalNode) => {
      const type = $isHeadingNode(selectedElement)
        ? selectedElement.getTag()
        : selectedElement.getType();

      if (type in blockTypeToBlockName) {
        setToolbarState((prevState) => ({
          ...prevState,
          blockType: type as keyof typeof blockTypeToBlockName,
        }));
      }
    },
    [setToolbarState]
  );

  const updateToolbar = useCallback(() => {
    try {
      // Check if editor is in a valid state
      if (!editor || !editor.isEditable()) {
        return;
      }

      const selection = $getSelection();

      // Handle range selections
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const element = findTopLevelElement(anchorNode);
        const elementKey = element.getKey();
        const elementDOM = editor.getElementByKey(elementKey);
        const node = getSelectedNode(selection);

        const tableNode = $findMatchingParent(node, $isTableNode);
        if ($isTableNode(tableNode)) {
          setToolbarState((prevState) => ({ ...prevState, rootType: "table" }));
        } else {
          setToolbarState((prevState) => ({ ...prevState, rootType: "root" }));
        }

        if (elementDOM !== null) {
          if ($isListNode(element)) {
            const parentList = $getNearestNodeOfType<ListNode>(
              anchorNode,
              ListNode
            );
            const listType = parentList
              ? parentList.getListType()
              : element.getListType();

            const type = listType === "check" ? "bullet" : listType;
            setToolbarState((prevState) => ({
              ...prevState,
              blockType: type as "number" | "bullet",
            }));
          } else if ($isHeadingNode(element)) {
            handleHeadingNode(element);
          } else if (element.getType() === "paragraph") {
            setToolbarState((prevState) => ({
              ...prevState,
              blockType: "paragraph",
            }));
          }
        }

        // Update text format for range selections only
        try {
          setToolbarState((prevState) => ({
            ...prevState,
            isBold: selection.hasFormat("bold"),
            isItalic: selection.hasFormat("italic"),
            isUnderline: selection.hasFormat("underline"),
            isStrikethrough: selection.hasFormat("strikethrough"),
          }));
        } catch (formatError) {
          // If format checking fails, reset format states
          setToolbarState((prevState) => ({
            ...prevState,
            isBold: false,
            isItalic: false,
            isUnderline: false,
            isStrikethrough: false,
          }));
        }
      }
      // Handle table selections
      else if ($isTableSelection(selection)) {
        setToolbarState((prevState) => ({
          ...prevState,
          rootType: "table",
          isBold: false,
          isItalic: false,
          isUnderline: false,
          isStrikethrough: false,
        }));
      }
      // Handle node selections
      else if ($isNodeSelection(selection)) {
        const nodes = selection.getNodes();
        for (const selectedNode of nodes) {
          const parentList = $getNearestNodeOfType<ListNode>(
            selectedNode,
            ListNode
          );
          if (parentList) {
            const listType = parentList.getListType();
            const type = listType === "check" ? "bullet" : listType;
            setToolbarState((prevState) => ({
              ...prevState,
              blockType: type as "number" | "bullet",
            }));
          } else {
            const selectedElement = findTopLevelElement(selectedNode);
            handleHeadingNode(selectedElement);
          }
        }
      }
    } catch (error) {
      if (isLexicalContextError(error)) {
        handleLexicalContextError(error, "updateToolbar");
        return;
      }

      // Silently handle other errors to prevent crashes
      console.warn("Error updating toolbar:", error);
      captureSentryException(error, { extra: { toolbarState, editor } });
    }
  }, [editor, setToolbarState, handleHeadingNode]);

  useEffect(() => {
    if (!editor || !editor.isEditable()) {
      return;
    }

    // Add a small delay to ensure editor is fully initialized
    const timeoutId = setTimeout(() => {
      if (!editor || !editor.isEditable()) {
        return;
      }

      const unregister = mergeRegister(
        editor.registerUpdateListener(({ editorState }) => {
          try {
            editorState.read(() => {
              updateToolbar();
            });
          } catch (error) {
            if (isLexicalContextError(error)) {
              handleLexicalContextError(error, "update_listener");
              return;
            }

            console.error("Error in editor update listener:", error);
          }
        }),
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          (_payload, _newEditor) => {
            try {
              _newEditor.getEditorState().read(() => {
                updateToolbar();
              });
            } catch (error) {
              if (isLexicalContextError(error)) {
                handleLexicalContextError(error, "selection_change");
                return false;
              }

              console.warn("Error in selection change command:", error);
            }
            return false;
          },
          COMMAND_PRIORITY_LOW
        ),
        editor.registerCommand(
          CAN_UNDO_COMMAND,
          (payload) => {
            setCanUndo(payload);
            return false;
          },
          COMMAND_PRIORITY_LOW
        ),
        editor.registerCommand(
          CAN_REDO_COMMAND,
          (payload) => {
            setCanRedo(payload);
            return false;
          },
          COMMAND_PRIORITY_LOW
        )
      );

      return unregister;
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [editor, updateToolbar]);

  return (
    <div
      className={`toolbar flex items-center justify-end gap-2 ${padding} z-[1]`}
      ref={toolbarRef}
    >
      <ToolbarItem
        disabled={!canUndo}
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        className="spaced"
        ariaLabel="Undo"
        tooltip="Undo"
      >
        <Undo
          className="format undo"
          fontSize="small"
          sx={{ fontSize: "16px" }}
        />
      </ToolbarItem>
      <ToolbarItem
        disabled={!canRedo}
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        className="toolbar-item"
        ariaLabel="Redo"
        tooltip="Redo"
      >
        <Redo
          className="format redo"
          fontSize="small"
          sx={{ fontSize: "16px" }}
        />
      </ToolbarItem>
      <span className="text-sm text-gray-500 mx-1/2">|</span>
      <ToolBarSelect
        blockType={toolbarState.blockType}
        setToolbarState={setToolbarState}
        toolbarState={toolbarState}
      />
      <span className="text-sm text-gray-500 mx-1/2">|</span>
      <ToolbarItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
        }}
        className={"spaced "}
        ariaLabel="Format Bold"
        isActive={toolbarState.isBold}
        tooltip="Bold"
      >
        <FormatBold
          className="format bold"
          fontSize="small"
          sx={{ fontSize: "16px" }}
        />
      </ToolbarItem>
      <ToolbarItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
        }}
        className={"spaced "}
        ariaLabel="Format Italics"
        isActive={toolbarState.isItalic}
        tooltip="Italic"
      >
        <FormatItalic
          className="format italic"
          fontSize="small"
          sx={{ fontSize: "16px" }}
        />
      </ToolbarItem>
      <ToolbarItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
        }}
        className={"spaced "}
        ariaLabel="Format Underline"
        isActive={toolbarState.isUnderline}
        tooltip="Underline"
      >
        <FormatUnderlined
          className="format underline"
          fontSize="small"
          sx={{ fontSize: "16px" }}
        />
      </ToolbarItem>
      <ToolbarItem
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
        }}
        className={"spaced "}
        ariaLabel="Format Strikethrough"
        isActive={toolbarState.isStrikethrough}
        tooltip="Strikethrough"
      >
        <FormatStrikethrough
          className="format strikethrough"
          fontSize="small"
          sx={{ fontSize: "16px" }}
        />
      </ToolbarItem>
      <span className="text-sm text-gray-500 mx-1/2">|</span>

      <ToolbarItem
        ariaLabel="Insert Table"
        onClick={() => {
          setShowModal(true);
        }}
        className="item"
        tooltip="Insert Table"
      >
        <GridOn
          className="icon table"
          fontSize="small"
          sx={{ fontSize: "16px" }}
        />
      </ToolbarItem>
      <ToolbarItem
        ariaLabel="Insert Image"
        onClick={() => fileInputRef.current?.click()}
        tooltip="Insert Image"
      >
        <ImageOutlined fontSize="small" sx={{ fontSize: "16px" }} />
      </ToolbarItem>
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleImageUpload}
      />
      {showModal && (
        <Dialog
          onClose={() => setShowModal(false)}
          title="Insert Table"
          open={true}
        >
          <InsertTableDialog
            activeEditor={editor}
            onClose={() => setShowModal(false)}
          />
        </Dialog>
      )}
    </div>
  );
};

export default ToolbarPlugin;
